/**
 * Public-access message attachment helpers.
 *
 * Over public (internet) access, RPC is tunneled through a WebRTC DataChannel
 * (with the transport-level framing layer that splits messages > ~256 KiB). To
 * keep `sessions:sendMessage` payloads lean we drop attachment fields that are
 * redundant on the wire, then let the framing layer carry the rest.
 *
 * These run ONLY on the public-access path; LAN/desktop sends are untouched.
 * All functions are pure and return new objects (never mutate the inputs).
 */

import type { FileAttachment } from '@okclawpro/shared/protocol'
import type { StoredAttachment } from '@okclawpro/core/types'

/**
 * Prepare the `attachments` arg (the FileAttachment[] Claude actually consumes)
 * for public-access send. Keeps everything the agent needs — `base64`/`text`
 * content, `storedPath`/`markdownPath` references, and metadata — and drops the
 * `thumbnailBase64` preview, which the agent never reads and only inflates the
 * payload.
 */
export function preparePublicAccessMessageAttachments(
  attachments?: FileAttachment[],
): FileAttachment[] | undefined {
  if (!attachments) return undefined
  return attachments.map(({ thumbnailBase64: _thumbnailBase64, ...rest }) => ({ ...rest }))
}

/**
 * Strip redundant heavy payloads from the `storedAttachments` arg before sending
 * it over public access. `storedAttachments` is persisted into the user message
 * for history display, so `thumbnailBase64` is kept (other clients render it).
 * Only `resizedBase64` is dropped: it is the resized-image content for the Claude
 * API, which already arrives via the `attachments` arg, so the copy carried on
 * the stored attachment is pure redundancy on the wire.
 */
export function stripStoredAttachmentPayloads(
  storedAttachments?: StoredAttachment[],
): StoredAttachment[] | undefined {
  if (!storedAttachments) return undefined
  return storedAttachments.map(({ resizedBase64: _resizedBase64, ...rest }) => ({ ...rest }))
}
